import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { clearAdminSession, getAdminSession } from "../../utils/adminAuth";
import { loadHubSpotIds } from "../../utils/hubspotIds";

type NavItem =
  | { divider: true }
  | { heading: string }
  | { to: string; label: string; icon: string };

const NAV_ITEMS: NavItem[] = [
  { to: "/admin/dashboard",           label: "Dashboard",              icon: "bi-speedometer2" },
  { divider: true },
  { heading: "Membership" },
  { to: "/admin/transactions",        label: "Memberships",             icon: "bi-receipt" },
  { to: "/admin/gift-cards",          label: "Gift Cards",              icon: "bi-gift" },
  { to: "/admin/refunds",             label: "Refunds",                 icon: "bi-arrow-counterclockwise" },
  { to: "/admin/redemptions",         label: "Redemptions",             icon: "bi-check2-circle" },
  { to: "/admin/promo-codes",         label: "Promo Codes",             icon: "bi-tag" },
  { divider: true },
  { heading: "Add-ons" },
  { to: "/admin/replacement-card",    label: "Replacement VIP Card",    icon: "bi-credit-card" },
  { to: "/admin/bag-tag",             label: "Personalized Bag Tag",    icon: "bi-tag" },
  { to: "/admin/bronze-addon",        label: "Bronze Package Add-On",   icon: "bi-plus-circle" },
  { divider: true },
  { heading: "Website Settings" },
  { to: "/admin/web-page-content", label: "Web Page Content", icon: "bi-file-earmark-text" },
  { divider: true },
  { heading: "Bulk Actions" },
  { to: "/admin/bulk-upload",          label: "Bulk Upload",             icon: "bi-cloud-upload" },
  { to: "/admin/bulk-vip-packages",   label: "VIP Packages",            icon: "bi-box-seam" },
  { to: "/admin/bulk-bag-tags",       label: "Bag Tags",                icon: "bi-tags" },
  { divider: true },
  { heading: "System" },
  { to: "/admin/annual-dates",         label: "Annual Dates",            icon: "bi-calendar-range" },
  { to: "/admin/audit-log",           label: "Audit Log",               icon: "bi-clock-history" },
];

const SIDEBAR_EXPANDED  = 240;
const SIDEBAR_COLLAPSED = 60;
const TOPBAR_HEIGHT     = 56;
const BRAND_GREEN       = "#023822";
const BRAND_GOLD        = "#C9A24D";

const STORAGE_KEY = "admin_sidebar_collapsed";


const AdminLayout = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [idsLoaded, setIdsLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const toggleRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const session = getAdminSession();
    if (!session) {
      navigate("/admin/login", { replace: true });
    } else {
      setChecked(true);
      // Sandbox/production HubSpot IDs used by the pages' status badges and HubSpot links
      loadHubSpotIds().finally(() => setIdsLoaded(true));
    }
  }, [navigate]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate("/admin/login", { replace: true });
  };

  if (!checked) return null;

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Top bar */}
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: TOPBAR_HEIGHT,
          backgroundColor: BRAND_GREEN,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 1.25rem",
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}
      >
        <NavLink
          to="/admin/dashboard"
          style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "0.6rem" }}
        >
          <img
            src="/assets/vsga-logo.png"
            alt="VSGA"
            style={{ height: 36, width: 36, objectFit: "contain" }}
          />
          {!collapsed && (
            <span
              style={{
                color: BRAND_GOLD,
                fontWeight: 700,
                fontSize: "0.95rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              VSGA Admin
            </span>
          )}
        </NavLink>

        {/* Right side — audit log + logout */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <NavLink
            to="/admin/audit-log"
            title="Audit Log"
            style={({ isActive }) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isActive ? "rgba(201,162,77,0.18)" : "transparent",
              border: `1px solid rgba(201,162,77,0.45)`,
              color: BRAND_GOLD,
              fontSize: "1rem",
              lineHeight: 1,
              padding: "0.3rem 0.55rem",
              borderRadius: 6,
              textDecoration: "none",
            })}
          >
            <i className="bi bi-clock-history" />
          </NavLink>

          <button
            onClick={handleLogout}
            className="btn btn-sm"
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${BRAND_GOLD}`,
              color: BRAND_GOLD,
              fontSize: "0.8rem",
              letterSpacing: "0.05em",
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Body below topbar */}
      <div style={{ display: "flex", flex: 1, paddingTop: TOPBAR_HEIGHT }}>
        {/* Sidebar */}
        <nav
          style={{
            position: "fixed",
            top: TOPBAR_HEIGHT,
            left: 0,
            bottom: 0,
            width: sidebarWidth,
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #dee2e6",
            overflowX: "hidden",
            transition: "width 0.2s ease",
          }}
        >
          {/* Scrollable nav items — calc leaves 46px at bottom for the toggle button */}
          <div style={{ height: "calc(100% - 46px)", overflowY: "auto", overflowX: "hidden", paddingTop: "1rem" }}>
            <ul className="nav flex-column" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {NAV_ITEMS.map((item, idx) =>
                "divider" in item ? (
                  collapsed ? null : (
                    <li key={`divider-${idx}`}>
                      <hr style={{ margin: "0.5rem 1.25rem", borderColor: "#dee2e6" }} />
                    </li>
                  )
                ) : "heading" in item ? (
                  collapsed ? null : (
                    <li
                      key={`heading-${idx}`}
                      style={{
                        padding: "0.35rem 1rem",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "#6c757d",
                        backgroundColor: "#e9ecef",
                        borderTop: "1px solid #dee2e6",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      {item.heading}
                    </li>
                  )
                ) : (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      title={collapsed ? item.label : undefined}
                      style={({ isActive }) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: collapsed ? 0 : "0.6rem",
                        justifyContent: collapsed ? "center" : "flex-start",
                        padding: collapsed ? "0.65rem 0" : "0.45rem 0.75rem 0.45rem 1rem",
                        fontSize: "0.78rem",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? BRAND_GREEN : "#495057",
                        backgroundColor: isActive ? "rgba(2,56,34,0.07)" : "transparent",
                        borderLeft: collapsed ? "none" : (isActive ? `3px solid ${BRAND_GREEN}` : "3px solid transparent"),
                        borderRight: collapsed && isActive ? `3px solid ${BRAND_GREEN}` : "none",
                        textDecoration: "none",
                        transition: "background 0.15s, color 0.15s",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      })}
                    >
                      <i
                        className={`bi ${item.icon}`}
                        style={{ fontSize: "0.875rem", flexShrink: 0 }}
                      />
                      {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Collapse toggle — always at bottom */}
          <div style={{ height: 46, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-end", padding: "0 0.75rem", borderTop: "1px solid #dee2e6" }}>
            <button
              ref={toggleRef}
              onClick={toggle}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                background: "#e9ecef",
                border: "1px solid #dee2e6",
                borderRadius: 6,
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#495057",
                padding: 0,
              }}
            >
              <i className={`bi ${collapsed ? "bi-chevron-double-right" : "bi-chevron-double-left"}`} style={{ fontSize: "0.75rem" }} />
            </button>
          </div>
        </nav>

        {/* Main content */}
        <main
          style={{
            marginLeft: sidebarWidth,
            flex: 1,
            padding: "2rem",
            minWidth: 0,
            transition: "margin-left 0.2s ease",
          }}
        >
          {idsLoaded ? <Outlet /> : null}
        </main>
      </div>

    </div>
  );
};

export default AdminLayout;
